/**
 * Dashboard locale configuration.
 *
 * Keep this file small and dependency-free; it's imported from both
 * client and (occasionally) build-time code paths.
 */

import { SupportedLocale as GraphqlSupportedLocale } from '../types/graphql';

export enum SupportedLocale {
  EnUs = 'en-US',
  EsEs = 'es-ES',
}

export const SUPPORTED_LOCALES = Object.values(SupportedLocale);

export const DEFAULT_LOCALE: SupportedLocale = SupportedLocale.EnUs;

export const LOCALE_COOKIE = 'TT_LOCALE';

export const LOCALE_DISPLAY_NAMES: Record<SupportedLocale, string> = {
  [SupportedLocale.EnUs]: 'English (US)',
  [SupportedLocale.EsEs]: 'Español (España)',
};

export const isSupportedLocale = (value: unknown): value is SupportedLocale =>
  typeof value === 'string' && (SUPPORTED_LOCALES as string[]).includes(value);

export const normaliseLocale = (value: unknown): SupportedLocale => {
  if (isSupportedLocale(value)) {
    return value;
  }
  return DEFAULT_LOCALE;
};

/**
 * GraphQL exposes `SupportedLocale` enum as TypeScript keys (`EnUs`, `EsEs`)
 * but the app uses the locale-code values (`en-US`, `es-ES`) for cookies,
 * `Intl` APIs and `next-intl`. Use these mappers when crossing the wire.
 */
const APP_TO_GRAPHQL: Record<SupportedLocale, GraphqlSupportedLocale> = {
  [SupportedLocale.EnUs]: GraphqlSupportedLocale.EnUs,
  [SupportedLocale.EsEs]: GraphqlSupportedLocale.EsEs,
};

const GRAPHQL_TO_APP: Record<GraphqlSupportedLocale, SupportedLocale> = {
  [GraphqlSupportedLocale.EnUs]: SupportedLocale.EnUs,
  [GraphqlSupportedLocale.EsEs]: SupportedLocale.EsEs,
};

export const toGraphqlLocale = (locale: SupportedLocale): GraphqlSupportedLocale =>
  APP_TO_GRAPHQL[locale];

export const fromGraphqlLocale = (locale: GraphqlSupportedLocale): SupportedLocale =>
  GRAPHQL_TO_APP[locale];
